import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { User, Lock, Mail, LogOut } from 'lucide-react';
import { useTranslation } from '../i18n';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfile = ({ user, onLogout, lang = 'en' }) => {
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('profile.passwordsDoNotMatch'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error(t('profile.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/password-reset/change`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        user_email: user.email
      });
      
      toast.success(t('profile.passwordChanged'));
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowChangePassword(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || t('profile.passwordChangeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutClick = () => {
    onLogout();
    navigate('/');
  };

  return (
    <>
      <SEOHead 
        title="My Profile | AAA of Miami"
        description="Manage your profile at AAA of Miami"
      />
      <div className="pt-20 min-h-screen px-4 py-12" style={{ background: 'var(--bg-secondary)' }}>
      <div className="max-w-2xl mx-auto">
        {/* Profile Header */}
        <div className="rounded-lg shadow-xl p-8 mb-6" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center mb-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mr-6" style={{ background: 'var(--accent)' }}>
              <User className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
                {user?.username || t('common.loading')}
              </h1>
              <p className="flex items-center mt-2" style={{ color: 'var(--text-secondary)' }}>
                <Mail className="w-4 h-4 mr-2" />
                {user?.email}
              </p>
            </div>
          </div>

          {/* Profile Details */}
          <div className="grid gap-4 mb-6">
            <div className="p-4 rounded" style={{ background: 'var(--bg-secondary)' }}>
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{t('profile.username')}</label>
              <p className="text-lg mt-1" style={{ color: 'var(--text-primary)' }}>{user?.username}</p>
            </div>

            <div className="p-4 rounded" style={{ background: 'var(--bg-secondary)' }}>
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{t('profile.email')}</label>
              <p className="text-lg mt-1" style={{ color: 'var(--text-primary)' }}>{user?.email}</p>
            </div>

            <div className="p-4 rounded" style={{ background: 'var(--bg-secondary)' }}>
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{t('profile.membershipType')}</label>
              <p className="text-lg mt-1" style={{ color: 'var(--text-primary)' }}>
                {user?.is_admin ? t('profile.admin') : t('profile.standardUser')}
              </p>
            </div>
          </div>

          {/* Change Password Section */}
          <div className="border-t pt-6" style={{ borderColor: 'var(--border-color)' }}>
            {!showChangePassword ? (
              <Button 
                onClick={() => setShowChangePassword(true)}
                variant="outline"
                className="w-full"
              >
                <Lock className="w-4 h-4 mr-2" />
                {t('profile.changePasswordButton')}
              </Button>
            ) : (
              <div>
                <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                  {t('profile.changePassword')}
                </h3>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      {t('profile.currentPassword')}
                    </label>
                    <Input
                      type="password"
                      placeholder={t('profile.enterCurrentPassword')}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      {t('profile.newPassword')}
                    </label>
                    <Input
                      type="password"
                      placeholder={t('profile.enterNewPassword')}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                      required
                      minLength={6}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      {t('profile.confirmNewPassword')}
                    </label>
                    <Input
                      type="password"
                      placeholder={t('profile.reenterNewPassword')}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                      required
                      minLength={6}
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button type="submit" className="flex-1" disabled={loading}>
                      {loading ? t('profile.changing') : t('profile.changePasswordButton')}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setShowChangePassword(false);
                        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      }}
                      disabled={loading}
                    >
                      {t('profile.cancel')}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Logout Button */}
        <Button 
          onClick={handleLogoutClick}
          variant="outline"
          className="w-full"
          style={{ borderColor: '#dc2626', color: '#dc2626' }}
        >
          <LogOut className="w-4 h-4 mr-2" />
          {t('profile.logout')}
        </Button>
      </div>
    </div>
    </>
  );
};

export default UserProfile;
